import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { usePosterCardAnalytics } from "../../hooks/usePosterCardAnalytics";
import { trackPosterClick } from "../../utils/curatorAnalytics";
import { formatPosterScoreLine, formatPersonInitials, getPreferredServiceIcon, getServiceInitial, effectiveInTheatersForDisplay } from "../appleTvPosterMeta";
import {
  POSTER_LAYOUT_HORIZONTAL,
  getStoredPosterLayout,
  subscribePosterLayout,
} from "../applePosterLayout";
import GlyphIcon from "./GlyphIcon";
import HeroTrailerDetails from "./HeroTrailerDetails";

// Tracks the device-wide poster layout preference (portrait posters vs. wide
// backdrops) and re-renders cards live when it changes in settings.
function usePosterLayout() {
  const [layout, setLayout] = useState(getStoredPosterLayout);
  useEffect(() => subscribePosterLayout(setLayout), []);
  return layout;
}

// Shared Apple TV poster card. Replaces nine hand-rolled copies across the
// /apple route pages. Renders identical DOM (same class names) so the
// existing CSS in AppleTvClone.css continues to apply.
//
// State variants:
//   "ready"    - normal card with image + meta + (optional) rank
//   "skeleton" - shimmer placeholder of the same shape so shelves do not
//                 jump when data lands; used by per-shelf loading states.
//   "error"    - inline error tile shown when a shelf failed to load.
//
// Convention for the optional meta-line props (`serviceLine`, `scoreLine`):
//   prop = undefined - use the default rendering (service label, score line)
//   prop = null      - omit that line entirely
//   prop = ReactNode - render the supplied node

const DEFAULT = Symbol("default");

// Small "where to watch" chip pinned to the bottom-right of the poster on the
// score line. Streaming providers show their branded logo; theatrical titles
// show the popcorn glyph and buy/rent-only titles show the shopping-bag glyph.
function ServiceBadge({ item }) {
  const inTheaters = effectiveInTheatersForDisplay(item?.watchSources, Boolean(item?.inTheaters));
  const { label, iconUrl, kind } = getPreferredServiceIcon(
    item?.watchSources,
    inTheaters
  );
  if (iconUrl) {
    return (
      <img
        className="apple-clone__service-badge"
        src={iconUrl}
        alt={label}
        title={label}
        loading="eager"
        decoding="async"
      />
    );
  }
  if (kind === "theaters") {
    return (
      <span className="apple-clone__service-badge apple-clone__service-badge--glyph" title={label}>
        <GlyphIcon name="popcorn" title={label} />
      </span>
    );
  }
  if (kind === "buy") {
    return (
      <span className="apple-clone__service-badge apple-clone__service-badge--buy-round" title={label} aria-label={label}>
        <GlyphIcon name="bag" title={label} />
      </span>
    );
  }
  return (
    <span
      className="apple-clone__service-badge apple-clone__service-badge--text apple-clone__service-badge--initial"
      title={label}
      aria-label={label}
    >
      {getServiceInitial(label)}
    </span>
  );
}

function renderServiceBadge(item, serviceLine) {
  if (serviceLine === null) return null;
  if (serviceLine !== undefined && serviceLine !== DEFAULT) return serviceLine;
  return <ServiceBadge item={item} />;
}

function defaultScoreLine(item) {
  return formatPosterScoreLine(item);
}

// Split long backdrop titles at the word boundary that best balances line length.
const BACKDROP_TITLE_BALANCE_MIN_CHARS = 26;

function balanceBackdropTitle(title) {
  const text = String(title || "").trim();
  if (!text) return text;

  const words = text.split(/\s+/).filter(Boolean);
  if (words.length < 2 || text.length <= BACKDROP_TITLE_BALANCE_MIN_CHARS) {
    return text;
  }

  let bestSplit = 1;
  let bestScore = Infinity;

  for (let i = 1; i < words.length; i += 1) {
    const line1 = words.slice(0, i).join(" ");
    const line2 = words.slice(i).join(" ");
    const diff = Math.abs(line1.length - line2.length);
    const minLine = Math.min(line1.length, line2.length);
    const orphanPenalty = minLine < 5 ? 12 : 0;
    const score = diff + orphanPenalty;
    if (score < bestScore) {
      bestScore = score;
      bestSplit = i;
    }
  }

  const line1 = words.slice(0, bestSplit).join(" ");
  const line2 = words.slice(bestSplit).join(" ");

  return (
    <>
      {line1}
      <br aria-hidden="true" />
      {line2}
    </>
  );
}

function PersonCardTitle({ title, to, onLinkClick, balanced = false }) {
  const label = title || "";
  if (balanced) {
    return <BackdropCardTitle title={label} to={to} onLinkClick={onLinkClick} />;
  }
  if (to) {
    return (
      <Link
        to={to}
        className="apple-clone__card-title"
        title={label}
        onClick={onLinkClick}
      >
        {label}
      </Link>
    );
  }
  return (
    <span className="apple-clone__card-title" title={label}>
      {label}
    </span>
  );
}

function BackdropCardTitle({ title, to, onLinkClick }) {
  const label = title || "";
  const content = balanceBackdropTitle(label);
  if (to) {
    return (
      <Link
        to={to}
        className="apple-clone__card-title"
        title={label}
        onClick={onLinkClick}
      >
        {content}
      </Link>
    );
  }
  return (
    <span className="apple-clone__card-title" title={label}>
      {content}
    </span>
  );
}

function PosterImage({ item, backdrop, controlled = false, hold = false, onSettled, loading = "lazy", progressive = false, reveal = true, revealDelay = 0 }) {
  const [loadedSrc, setLoadedSrc] = useState(null);
  const src = backdrop
    ? (item?.backdrop || item?.poster)
    : (item?.poster || item?.backdrop);
  const isPerson = item?.mediaType === "person";

  // Report "settled" exactly once per src so the parent's ordered queue advances
  // (load, error, timeout, or no-image). Reset when enrichment swaps the URL.
  const settledRef = useRef(false);
  const settledSrcRef = useRef(src);
  if (settledSrcRef.current !== src) {
    settledSrcRef.current = src;
    settledRef.current = false;
  }
  const settle = useCallback(() => {
    if (settledRef.current) return;
    settledRef.current = true;
    if (typeof onSettled === "function") onSettled();
  }, [onSettled]);

  // A card with no image still needs to release the queue immediately.
  useEffect(() => {
    if (controlled && !src) settle();
  }, [controlled, src, settle]);

  // Safety valve: a stalled/slow image must not freeze the whole shelf.
  useEffect(() => {
    if ((!controlled && !progressive) || hold || !src) return undefined;
    const timer = setTimeout(settle, 2500);
    return () => clearTimeout(timer);
  }, [controlled, progressive, hold, src, settle]);

  if (progressive) {
    const visible = loadedSrc === src && reveal;
    return <>
      <span className={`apple-clone__poster-loading-title${visible ? ' is-revealed' : ''}`} style={{transitionDelay:`${revealDelay}ms`}} aria-hidden="true">{item?.title || ''}</span>
      {src ? <img src={src} alt={item?.title || ''} loading={loading} fetchPriority={loading === "eager" ? "high" : "auto"} decoding="async"
        className={`apple-clone__poster-art--progressive${visible ? ' is-revealed' : ''}`}
        style={{transitionDelay:`${revealDelay}ms`}}
        onLoad={event => {
          const image = event.currentTarget;
          Promise.resolve(typeof image.decode === 'function' ? image.decode() : undefined).catch(() => {}).then(() => { setLoadedSrc(src); settle(); });
        }} onError={settle} /> : null}
    </>;
  }
  if (src) {
    if (controlled) {
      // Held until it is this card's turn; renders nothing over the network so
      // backdrops fill in strict left-to-right order once released.
      if (hold) {
        return <span className="apple-clone__poster-pending" aria-hidden="true" />;
      }
      return (
        <img
          key={src}
          src={src}
          alt={item?.title || ""}
          decoding="async"
          onLoad={settle}
          onError={settle}
        />
      );
    }
    return <img src={src} alt={item?.title || ""} loading={loading} decoding="async" />;
  }
  if (isPerson) {
    return (
      <span className="apple-clone__poster-fallback apple-clone__poster-fallback--person" aria-hidden="true">
        {formatPersonInitials(item?.title || item?.name)}
      </span>
    );
  }
  return <span className="apple-clone__poster-fallback">{item?.title || "Poster"}</span>;
}

function MetaLines({ item, serviceLine, scoreLine, creditLine }) {
  const showServiceLine = serviceLine !== null;
  const resolvedScoreLine = scoreLine === null
    ? null
    : (scoreLine === undefined || scoreLine === DEFAULT
      ? defaultScoreLine(item)
      : scoreLine);
  const showScoreLine = scoreLine !== null && Boolean(resolvedScoreLine);
  const renderedServiceBadge = serviceLine === undefined || serviceLine === DEFAULT
    ? <ServiceBadge item={item} />
    : serviceLine;
  const titleLabel = String(item?.title || item?.name || "").trim();
  const awardEyebrow = String(item?.awardEyebrow || "").trim();
  return (
    <div className="apple-clone__meta">
      {awardEyebrow ? (
        <div
          className={`apple-clone__card-eyebrow${item?.awardWon ? " apple-clone__card-eyebrow--winner" : ""}`}
        >
          {awardEyebrow}
        </div>
      ) : null}
      {titleLabel ? (
        <div className="apple-clone__meta-title" title={titleLabel}>
          {titleLabel}
        </div>
      ) : null}
      {creditLine ? (
        <div className="apple-clone__creditline">{creditLine}</div>
      ) : null}
      {showScoreLine || showServiceLine ? (
        <div className="apple-clone__scorerow">
          <span className="apple-clone__scoreline">
            {showScoreLine ? resolvedScoreLine : null}
          </span>
          {showServiceLine ? (
            <span className="apple-clone__service-slot">{renderedServiceBadge}</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function featuredHighlightTag(item) {
  const label = String(item?.heroMetricLabel || "").trim();
  if (label) return label;
  if (item?.oscarWinner) return "Oscar Winner";
  if (effectiveInTheatersForDisplay(item?.watchSources, Boolean(item?.inTheaters))) return "In Theaters";
  return null;
}

function featuredYear(item) {
  if (item?.releaseDate) return String(item.releaseDate).slice(0, 4);
  const subtitle = String(item?.subtitle || "").trim();
  if (/^\d{4}/.test(subtitle)) return subtitle.slice(0, 4);
  return subtitle || null;
}

function featuredGenres(item) {
  if (Array.isArray(item?.genres) && item.genres.length) {
    return item.genres.map((genre) => String(genre).trim()).filter(Boolean);
  }
  return item?.genre ? [String(item.genre).trim()] : [];
}

/**
 * Corner Curator score — faded white digits with a soft score-tint halo.
 * Low → warm coral/red tint; high → soft violet. Color only in the glow.
 */
function RankOverlay({ value }) {
  const score = Math.round(Number(value));
  const text = Number.isFinite(score) ? String(score) : "";
  const t = Number.isFinite(score) ? Math.max(0, Math.min(1, score / 100)) : 0;
  // Soft pastel hues: coral/red (12°) → violet (280°); tint reads in the glow.
  const hue = Math.round(12 + t * 268);
  const tone = `hsl(${hue} 62% 74%)`;
  return (
    <b
      className="apple-clone__rank apple-clone__rank--glaze"
      style={{ "--rank-tone": tone }}
    >
      {text}
    </b>
  );
}

function SkeletonCard({ featured = false, forceBackdrop = false, forcePoster = false, className = "" }) {
  const extra = className ? ` ${className}` : "";
  // Backdrop tiles keep title under the art; skip in-poster meta bars.
  const showFootMeta = forceBackdrop && !featured;
  return (
    <article
      className={`apple-clone__card apple-clone__card--poster apple-clone__card--skeleton${featured ? " apple-clone__card--featured" : ""}${forceBackdrop ? " apple-clone__card--backdrop" : ""}${forcePoster ? " apple-clone__card--force-poster" : ""}${extra}`}
      aria-hidden="true"
    >
      <div className="apple-clone__poster apple-clone__poster--skeleton">
        <div className="apple-skeleton apple-skeleton--poster" />
        {!forceBackdrop ? (
          <div className="apple-clone__meta apple-clone__meta--skeleton">
            <div className="apple-skeleton apple-skeleton--text" />
            <div className="apple-skeleton apple-skeleton--text apple-skeleton--text-short" />
          </div>
        ) : null}
      </div>
      {showFootMeta ? (
        <div className="apple-clone__meta apple-clone__meta--skeleton apple-clone__meta--skeleton-foot">
          <div className="apple-skeleton apple-skeleton--text" />
          <div className="apple-skeleton apple-skeleton--text apple-skeleton--text-short" />
        </div>
      ) : null}
    </article>
  );
}

function ErrorCard({ message, onRetry = null }) {
  return (
    <article className="apple-clone__card apple-clone__card--error" aria-live="polite">
      <div className="apple-clone__poster apple-clone__poster--error">
        <span>{message || "Couldn\u2019t load this list."}</span>
        {typeof onRetry === "function" ? (
          <button
            type="button"
            className="apple-clone__shelf-retry"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onRetry();
            }}
          >
            Retry
          </button>
        ) : null}
      </div>
    </article>
  );
}

export default function PosterCard(props) {
  const {
    item,
    to,
    onLinkClick,
    rank,
    scoreLine,
    serviceLine,
    creditLine,
    state = "ready",
    className,
    featured = false,
    featuredRecommendation = false,
    forceBackdrop = false,
    forcePoster = false,
    /** Match home carousel meta: ★ score · Genres · streaming icon. */
    carouselDockLine = false,
    /** When true, keep featured art/poster chrome but skip the built-in title overlay. */
    hideFeaturedOverlay = false,
    analytics,
    canLoadImage = true,
    imageLoading = "lazy",
    onImageSettled,
    progressiveImage = false,
    revealImage = true,
    imageRevealDelay = 0,
  } = props;

  // The shelf can drive ordered/progressive image loading by supplying an
  // `onImageSettled` callback; without it, images load lazily as before.
  const imageControlled = typeof onImageSettled === "function";

  const layout = usePosterLayout();
  const cardRef = usePosterCardAnalytics(analytics, item);

  const handleLinkClick = useCallback((event) => {
    if (analytics && item) {
      trackPosterClick({
        page: analytics.page,
        shelf: analytics.shelf,
        index: analytics.index,
        tmdbId: item.tmdbId || item.id,
        title: item.title || item.name,
        mediaType: item.mediaType,
      });
    }
    if (typeof onLinkClick === "function") onLinkClick(event);
  }, [analytics, item, onLinkClick]);

  if (state === "skeleton") {
    return (
      <SkeletonCard
        featured={featured}
        forceBackdrop={forceBackdrop}
        forcePoster={forcePoster}
        className={className}
      />
    );
  }
  if (state === "error") {
    return <ErrorCard message={props.errorMessage} onRetry={props.onRetry} />;
  }
  if (!item) return null;

  // Hide corner score/rank when missing or zero (Number(null) === 0).
  const showRank = rank != null && Number.isFinite(Number(rank)) && Number(rank) > 0;
  const isPersonCard = item?.mediaType === "person";
  const personTitle = item?.title || item?.name || "";
  const personSubtitle = isPersonCard
    ? (String(item?.knownForDepartment || "").trim()
      || (String(item?.subtitle || "").trim() !== "Person" ? String(item?.subtitle || "").trim() : ""))
    : "";
  const useBackdropLayout = !forcePoster && (featured || forceBackdrop || layout === POSTER_LAYOUT_HORIZONTAL);
  const posterUsesBackdropArt = useBackdropLayout;
  const showCardFoot = useBackdropLayout && !featured;
  const isBackdropCard = showCardFoot;
  const showPosterCornerService = serviceLine !== null && !featured && !isPersonCard;
  const metaServiceLine = showPosterCornerService ? null : serviceLine;
  const cornerServiceBadge = showPosterCornerService
    ? renderServiceBadge(item, serviceLine)
    : null;

  // Backdrop layout overlays score/meta on the art. Respect explicit scoreLine
  // props (e.g. account page "Your score" shelves); otherwise fall back to the
  // curator score line.
  const backdropScoreText = !isBackdropCard
    ? null
    : scoreLine === null
      ? null
      : scoreLine === undefined || scoreLine === DEFAULT
        ? defaultScoreLine(item)
        : scoreLine;

  const hasCreditOnArt = Boolean(creditLine) && !posterUsesBackdropArt && !featured && !isPersonCard;

  return (
    <article
      ref={cardRef}
      className={`apple-clone__card apple-clone__card--poster${isBackdropCard ? " apple-clone__card--backdrop" : ""}${isPersonCard ? " apple-clone__card--person" : ""}${forcePoster ? " apple-clone__card--force-poster" : ""}${featured ? " apple-clone__card--featured" : ""}${featuredRecommendation ? " apple-clone__card--featured-rec" : ""}${hasCreditOnArt ? " apple-clone__card--has-creditline" : ""}${className ? ` ${className}` : ""}`}
    >
      <div className="apple-clone__poster">
        {to ? (
          <Link
            to={to}
            className="apple-clone__poster-link"
            aria-label={`Open ${item.title || ""}`}
            onClick={handleLinkClick}
          />
        ) : null}
        {/* Blurred full-bleed fill behind the centered sharp backdrop (wide promo). */}
        {featured && (item.backdrop || item.poster) ? (
          <div className="apple-clone__featured-backdrop-blur" aria-hidden="true">
            <img
              src={item.backdrop || item.poster}
              alt=""
              loading="lazy"
              decoding="async"
            />
          </div>
        ) : null}
        {/* Zoom only the art; overlays stay fixed in the poster frame. */}
        <div className="apple-clone__poster-zoom">
          <PosterImage
            item={item}
            backdrop={posterUsesBackdropArt}
            controlled={imageControlled}
            hold={imageControlled && !canLoadImage}
            onSettled={onImageSettled}
            loading={imageLoading}
            progressive={progressiveImage}
            reveal={revealImage}
            revealDelay={imageRevealDelay}
          />
        </div>
        <div className="apple-clone__shade" />
        {showRank ? <RankOverlay value={rank} /> : null}
        {cornerServiceBadge ? (
          <div className="apple-clone__poster-service-corner">
            {cornerServiceBadge}
          </div>
        ) : null}
        {!posterUsesBackdropArt && isPersonCard ? (
          <MetaLines
            item={{ ...item, title: personTitle }}
            serviceLine={null}
            scoreLine={null}
            creditLine={personSubtitle || null}
          />
        ) : null}
        {!posterUsesBackdropArt && !isPersonCard ? (
          <MetaLines
            item={item}
            serviceLine={metaServiceLine}
            scoreLine={scoreLine}
            creditLine={creditLine}
          />
        ) : null}
        {featured && !hideFeaturedOverlay ? (
          <div className="apple-clone__featured-card-overlay">
            <HeroTrailerDetails
              highlightTag={carouselDockLine ? null : featuredHighlightTag(item)}
              title={item.title || ""}
              serviceBadge={getPreferredServiceIcon(
                item.watchSources,
                effectiveInTheatersForDisplay(item.watchSources, Boolean(item.inTheaters))
              )}
              mediaType={item.mediaType || "movie"}
              genres={featuredGenres(item)}
              genre={item.genre || ""}
              certification={item.certification || null}
              year={featuredYear(item)}
              yearLabel={(item.mediaType === "tv" || item.mediaType === "tv-show") ? (item.subtitle || null) : null}
              runtime={item.runtime ?? null}
              score={item.score ?? null}
              ratingsCount={item.ratingsCount ?? 0}
              tagline={React.isValidElement(item.tagline) ? item.tagline : String(item.tagline || "").trim()}
              featuredCard
              hideMediaType={false}
              combineServiceWithFacts={false}
              carouselDockLine={carouselDockLine}
            />
          </div>
        ) : null}
        {featured && (item.poster || item.backdrop) ? (
          <div className="apple-clone__featured-hero-poster" aria-hidden="true">
            <img
              src={item.poster || item.backdrop}
              alt=""
              loading="lazy"
              decoding="async"
            />
          </div>
        ) : null}
      </div>
      {showCardFoot ? (
        <div className="apple-clone__card-foot">
          <div className="apple-clone__card-foot-text">
            {!isPersonCard && item.awardEyebrow ? (
              <div
                className={`apple-clone__card-eyebrow${item.awardWon ? " apple-clone__card-eyebrow--winner" : ""}`}
              >
                {item.awardEyebrow}
              </div>
            ) : null}
            <PersonCardTitle
              title={isPersonCard ? personTitle : item.title}
              to={to}
              onLinkClick={handleLinkClick}
              balanced={!isPersonCard}
            />
            {isPersonCard && personSubtitle ? (
              <div className="apple-clone__card-person-meta">{personSubtitle}</div>
            ) : null}
            {!isPersonCard && creditLine ? (
              <div className="apple-clone__card-creditline">{creditLine}</div>
            ) : null}
            {!isPersonCard && backdropScoreText !== null ? (
              <div className="apple-clone__card-scoreline">
                <span>{backdropScoreText}</span>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </article>
  );
}
